import React, {
    useState, useEffect, useCallback, useMemo, CSSProperties,
} from 'react';
// Removed Row/Col imports
import Icon, { CameraOutlined, TagOutlined, SelectOutlined } from '@ant-design/icons';
import Slider from 'antd/lib/slider';
import InputNumber from 'antd/lib/input-number';
import Text from 'antd/lib/typography/Text';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Button from 'antd/lib/button';
import debounce from 'lodash/debounce';

import { RestoreIcon } from 'icons';
import CVATTooltip from 'components/common/cvat-tooltip';
import { clamp } from 'utils/math';
import GlobalHotKeys, { KeyMap } from 'utils/mousetrap-react';
import { Workspace, ThermalImageMode } from 'reducers';

interface Props {
    playing: boolean;
    frameNumber: number;
    currentFrameTimestampUtc: number; // in milliseconds
    thermalMode: string;
    minTemp: number;
    maxTemp: number;
    refFrame1: number;
    refFrame2: number;
    workspace: Workspace;
    onTempRenderChange(thermalMode: string, minTemp: number, maxTemp: number, refFrame1: number, refFrame2: number): void;

    /** Fired when timezone changes, with formatted values for convenience */
    onTimezoneChange?: (payload: {
        timeZone: string;
        formatted: string;
        zonedISO: string;
        offsetMinutes: number;
    }) => void;
}

const DEFAULT_TZ = 'Australia/Sydney';


/** Simple inline styles */
const toolbarStyle: CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 16,
    paddingTop: 12,       // space above labels
    paddingBottom: 4,    // optional — keeps bottom balanced
};

const blockStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
};

const labelStyle: CSSProperties = {
    fontSize: 12,
    color: 'rgba(0,0,0,.65)',
    marginBottom: 4,
    lineHeight: 1,
};


const valueStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  height: 32,
  display: 'flex',
  alignItems: 'center',
};

const numWidth = 90;
const tzWidth = 240;

/** Return all IANA timezones if supported, else a small fallback list */
const getAllTimeZones = (): string[] => {
    const anyIntl = Intl as any;
    if (anyIntl.supportedValuesOf) {
        try {
            return anyIntl.supportedValuesOf('timeZone') as string[];
        } catch {
            // fall through
        }
    }
    return [
        'UTC',
        'Europe/London',
        'Africa/Johannesburg',
        'Europe/Berlin',
        'America/New_York',
        'America/Los_Angeles',
        'Asia/Dubai',
        'Asia/Kolkata',
        'Asia/Tokyo',
        'Australia/Sydney',
    ];
};

/** Format a UTC epoch (ms) in a given TZ; also returns offset minutes and a label ISO-ish string */
const formatInTZ = (
    utcMs: number,
    timeZone: string,
): { formatted: string; zonedISO: string; offsetMinutes: number } => {
    const d = new Date(utcMs);

    const fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });

    const parts = fmt
        .formatToParts(d)
        .reduce<Record<string, string>>((acc, p) => {
            if (p.type !== 'literal') acc[p.type] = p.value;
            return acc;
        }, {});
    const yyyy = parts.year;
    const MM = parts.month;
    const dd = parts.day;
    const hh = parts.hour;
    const mm = parts.minute;
    const ss = parts.second;

    // e.g. "UTC+01:00" / "GMT+2"
    const tzNamePart = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'shortOffset',
    })
        .formatToParts(d)
        .find((p) => p.type === 'timeZoneName')?.value || 'UTC';

    let offsetMinutes = 0;
    const m = tzNamePart.match(/([+-])(\d{1,2})(?::?(\d{2}))?/);
    if (m) {
        const sign = m[1] === '-' ? -1 : 1;
        const h = parseInt(m[2], 10);
        const mins = m[3] ? parseInt(m[3], 10) : 0;
        offsetMinutes = sign * (h * 60 + mins);
    }

    const zonedISO = `${yyyy}-${MM}-${dd}T${hh}:${mm}:${ss}${tzNamePart.replace('UTC', 'GMT')}`;
    const formatted = `${yyyy}-${MM}-${dd} ${hh}:${mm}:${ss}`;

    return { formatted, zonedISO, offsetMinutes };
};

function ThermalImageControls(props: Props): JSX.Element {
    const {
        playing,
        frameNumber,
        currentFrameTimestampUtc,
        thermalMode,
        minTemp,
        maxTemp,
        refFrame1,
        refFrame2,
        workspace,
        onTempRenderChange,
        onTimezoneChange,
    } = props;

    const [thermalModeValue, setThermalModeValue] = useState<string>(thermalMode);
    const [minTempInputValue, setMinTempInputValue] = useState<number>(minTemp);
    const [maxTempInputValue, setMaxTempInputValue] = useState<number>(maxTemp);
    const [refFrame1InputValue, setRefFrame1InputValue] = useState<number>(refFrame1);
    const [refFrame2InputValue, setRefFrame2InputValue] = useState<number>(refFrame2);

    // Timezone UI state
    const [timeZone, setTimeZone] = useState<string>(DEFAULT_TZ);
    const timeZones = useMemo(() => getAllTimeZones(), []);

    const effectiveTimestampMs = typeof currentFrameTimestampUtc === 'number' ? currentFrameTimestampUtc : null;
    // const effectiveTimestampMs = 1754924906 * 1000;// hard coded for testing

    const onTempRenderChangeWrapper = useCallback(
        (newThermalMode, newMinTemp, newMaxTemp, newRef1, newRef2) => {
            onTempRenderChange(newThermalMode, newMinTemp, newMaxTemp, newRef1, newRef2);
        },
        [onTempRenderChange],
    );

    const debouncedTempRenderChange = useMemo(
        () => debounce(onTempRenderChangeWrapper, 500, { trailing: true }),
        [onTempRenderChangeWrapper],
    );

    useEffect(() => {
        if (thermalMode !== thermalModeValue) {
            setThermalModeValue(thermalMode);
        }
        if (minTemp !== minTempInputValue) {
            setMinTempInputValue(minTemp);
        }
        if (maxTemp !== maxTempInputValue) {
            setMaxTempInputValue(maxTemp);
        }
        if (refFrame1 !== refFrame1InputValue) {
            setRefFrame1InputValue(refFrame1);
        }
        if (refFrame2 !== refFrame2InputValue) {
            setRefFrame2InputValue(refFrame2);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thermalMode, minTemp, maxTemp, refFrame1, refFrame2]);

    useEffect(() => {
        return () => {
            debouncedTempRenderChange.cancel();
        };
    }, [debouncedTempRenderChange]);

    const { formatted: timeLabel } = useMemo(() => {
    if (effectiveTimestampMs !== null) {
        return formatInTZ(effectiveTimestampMs, timeZone);
    }
    return { formatted: 'unknown', zonedISO: '', offsetMinutes: 0 };
    }, [effectiveTimestampMs, timeZone]);

    const handleTZChange = (tz: string) => {
        setTimeZone(tz);
        if (onTimezoneChange && typeof currentFrameTimestampUtc === 'number') {
            const payload = formatInTZ(currentFrameTimestampUtc, tz);
            onTimezoneChange({ timeZone: tz, ...payload });
        }
    };

    return (
        <div style={toolbarStyle}>
            {/* Raw UNIX timestamp */}
            <div style={blockStyle}>
            <span style={labelStyle}>Timestamp</span>
            <div style={valueStyle}>
                {effectiveTimestampMs !== null ? effectiveTimestampMs : 'unknown'}
            </div>
            </div>

            {/* Formatted time */}
            <div style={blockStyle}>
            <span style={labelStyle}>Time</span>
            <div style={valueStyle}>{timeLabel}</div>
            </div>

            {/* Timezone selector */}
            <div style={blockStyle}>
            <span style={labelStyle}>Timezone</span>
            <CVATTooltip title="Display timestamps in this timezone">
                <Select
                showSearch
                optionFilterProp="children"
                value={timeZone}
                onChange={(tz: string) => {
                    setTimeZone(tz);
                    if (onTimezoneChange && effectiveTimestampMs !== null) {
                    const payload = formatInTZ(effectiveTimestampMs, tz);
                    onTimezoneChange({ timeZone: tz, ...payload });
                    }
                }}
                style={{ width: 240 }}
                >
                {timeZones.map((tz) => (
                    <Select.Option key={tz} value={tz}>
                    {tz}
                    </Select.Option>
                ))}
                </Select>
            </CVATTooltip>
            </div>



            <div style={blockStyle}>
                <span style={labelStyle}>Mode</span>
                <CVATTooltip title="Thermal Image Mode">
                    <Select
                        dropdownClassName="thermal-image-mode-selector-dropdown"
                        className="thermal-image-mode-selector"
                        value={thermalModeValue}
                        onChange={(value: string) => {
                            onTempRenderChange(
                                value,
                                minTempInputValue,
                                maxTempInputValue,
                                refFrame1InputValue,
                                refFrame2InputValue,
                            );
                        }}
                        style={{ width: 120 }}
                    >
                        {Object.values(ThermalImageMode).map((mode) => (
                            <Select.Option key={mode} value={mode}>
                                {mode}
                            </Select.Option>
                        ))}
                    </Select>
                </CVATTooltip>
            </div>

            <div style={blockStyle}>
                <span style={labelStyle}>Min Temp</span>
                <CVATTooltip title="Min Temp">
                    <InputNumber
                        className="thermal-image-minmaxtemp"
                        type="number"
                        value={minTempInputValue}
                        onChange={(value: number | string | null | undefined) => {
                            if (typeof value !== 'undefined' && value !== null) {
                                const newVal = Math.floor(clamp(+value, -128, maxTempInputValue - 1));
                                setMinTempInputValue(newVal);
                                debouncedTempRenderChange(
                                    thermalModeValue,
                                    newVal,
                                    maxTempInputValue,
                                    refFrame1InputValue,
                                    refFrame2InputValue,
                                );
                            }
                        }}
                        style={{ width: numWidth }}
                    />
                </CVATTooltip>
            </div>

            <div style={blockStyle}>
                <span style={labelStyle}>Max Temp</span>
                <CVATTooltip title="Max Temp">
                    <InputNumber
                        className="thermal-image-minmaxtemp"
                        type="number"
                        value={maxTempInputValue}
                        onChange={(value: number | string | null | undefined) => {
                            if (typeof value !== 'undefined' && value !== null) {
                                const newVal = Math.floor(clamp(+value, minTempInputValue + 1, 128));
                                setMaxTempInputValue(newVal);
                                debouncedTempRenderChange(
                                    thermalModeValue,
                                    minTempInputValue,
                                    newVal,
                                    refFrame1InputValue,
                                    refFrame2InputValue,
                                );
                            }
                        }}
                        style={{ width: numWidth }}
                    />
                </CVATTooltip>
            </div>

            <div style={blockStyle}>
                <span style={labelStyle}>Reference Frame</span>
                <CVATTooltip title="Frame to use when creating reference">
                    <InputNumber
                        className="thermal-image-minmaxtemp"
                        type="number"
                        readOnly
                        disabled={thermalModeValue === ThermalImageMode.RAW}
                        value={refFrame1}
                        style={{ width: numWidth }}
                    />
                </CVATTooltip>
            </div>

            <div style={blockStyle}>
                <span style={labelStyle}>&nbsp;</span>
                <CVATTooltip title="Set current frame as reference">
                    <Button
                        type="primary"
                        icon={<SelectOutlined />}
                        disabled={thermalModeValue === ThermalImageMode.RAW}
                        onClick={() => {
                            onTempRenderChange(
                                thermalModeValue,
                                minTempInputValue,
                                maxTempInputValue,
                                frameNumber,
                                frameNumber,
                            );
                        }}
                    >
                        Set Reference
                    </Button>
                </CVATTooltip>
            </div>


        </div>
    );
}

export default React.memo(ThermalImageControls);
